/** Radial vignette overlay that pulses at low health to signal danger. */
import Phaser from 'phaser';
import { ConfigLoader } from '../systems/ConfigLoader';
import { TAU } from '../utils/Math';
import { GAME_WIDTH, GAME_HEIGHT, SF0_X, SF0_Y } from '../utils/Constants';

/** Draws and animates a radial red vignette over the game viewport. */
export class VignetteOverlay {
  private scene: Phaser.Scene;
  private overlay: Phaser.GameObjects.Image;
  private baseAlpha: number = 0;
  private pulseTimer: number = 0;
  private isPulsing: boolean = false;
  private isCollapsing: boolean = false;
  private collapseStartAlpha: number = 0;
  private collapseDurationMs: number = 0;
  private collapseElapsed: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Generate a radial-gradient vignette texture via canvas
    const texKey = 'vignette-overlay';
    if (!scene.textures.exists(texKey)) {
      const size = 512;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;

      const gradient = ctx.createRadialGradient(
        size / 2, size / 2, size * 0.05,
        size / 2, size / 2, size * 0.50
      );
      gradient.addColorStop(0, 'rgba(128,0,0,0)');
      gradient.addColorStop(0.35, 'rgba(128,0,0,0)');
      gradient.addColorStop(0.7, 'rgba(128,0,0,0.4)');
      gradient.addColorStop(1, 'rgba(128,0,0,1)');

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size, size);

      scene.textures.addCanvas(texKey, canvas);
    }

    this.overlay = scene.add.image(SF0_X + GAME_WIDTH / 2, SF0_Y + GAME_HEIGHT / 2, texKey);
    this.overlay.setDisplaySize(GAME_WIDTH, GAME_HEIGHT);
    this.overlay.setScrollFactor(0);
    this.overlay.setDepth(900);
    this.overlay.setAlpha(0);
  }

  /** Update vignette intensity based on the player's current HP ratio (0–1). */
  setHealthRatio(ratio: number): void {
    const config = ConfigLoader.combat.screenEffects.lowHp;

    if (ratio < config.threshold && ratio > 0) {
      // Scale vignette intensity inversely with health within threshold
      const t = 1 - (ratio / config.threshold);
      this.baseAlpha = config.vignetteStrength * t;
      this.isPulsing = true;
      this.overlay.setAlpha(this.baseAlpha);
    } else {
      this.baseAlpha = 0;
      this.isPulsing = false;
      this.overlay.setAlpha(0);
    }
  }

  /** Begin the death collapse: vignette darkens steadily to full opacity over the given duration. */
  startDeathCollapse(durationMs: number): void {
    this.isCollapsing = true;
    this.isPulsing = false;
    this.collapseStartAlpha = this.overlay.alpha;
    this.collapseDurationMs = durationMs;
    this.collapseElapsed = 0;
  }

  /** Advance the pulse or death-collapse animation each frame (delta in ms). */
  update(delta: number): void {
    if (this.isCollapsing) {
      this.collapseElapsed += delta;
      const t = Math.min(this.collapseElapsed / this.collapseDurationMs, 1);
      const alpha = this.collapseStartAlpha + (1 - this.collapseStartAlpha) * t;
      this.overlay.setAlpha(alpha);
      return;
    }

    if (!this.isPulsing) return;

    const config = ConfigLoader.combat.screenEffects.lowHp;
    this.pulseTimer += delta / 1000;

    // Sinusoidal pulse between baseAlpha * 0.5 and baseAlpha
    const pulse = Math.sin(this.pulseTimer * config.pulseFrequencyHz * TAU);
    const alpha = this.baseAlpha * (0.75 + 0.25 * pulse);
    this.overlay.setAlpha(alpha);
  }

  /** Reset the vignette to its idle state (no pulse, no collapse, fully transparent). */
  reset(): void {
    this.isCollapsing = false;
    this.isPulsing = false;
    this.baseAlpha = 0;
    this.overlay.setAlpha(0);
  }

  /** Destroy the overlay image and free resources. */
  destroy(): void {
    this.overlay.destroy();
  }
}
